import { useMemo } from "react";
import { useUser } from "@/features/user/hooks/useUser";
import { useKyc } from "@/features/kyc";
import { useApplication } from "@/features/application/hooks";
import { useLoan } from "@/features/loan";
import { useLoanOffer, useOfferRequest } from "@/features/offer";
import { useOnboarding } from "@/features/onboarding";
import { useQuestionnaire } from "@/features/questionnaire";
import { useScoring } from "@/features/scoring";
import type { OnboardingFacts } from "../types/chat";
import { buildOnboardingFacts } from "../engine/facts";

interface UseOnboardingFactsReturn {
  facts: OnboardingFacts | null;
  isLoading: boolean;
}

export const useOnboardingFacts = ({
  fetchScoring,
}: {
  fetchScoring: boolean;
}): UseOnboardingFactsReturn => {
  const { user } = useUser();
  const { isKycLoading, isKycValid, isKycFailed } = useKyc(true);
  const { applicationState, isApplicationStateLoading } = useApplication(true);
  const { activeLoanSummary, isLoading: isLoanLoading } = useLoan(true);
  const { offer, isLoading: isOfferLoading } = useOfferRequest(true);
  const {
    isOnboardingStateLoading,
    isCompleted: isOnboardingCompleted,
    isEmailVerified,
    isTermsAccepted,
    onboardingState,
  } = useOnboarding(true);
  const { isQuestionnaireCompleted } = useQuestionnaire(true);
  const {
    loanOfferState,
    proofOfIncomeProcessing,
    isLoading: isLoanOfferLoading,
  } = useLoanOffer(true);
  const { scoringState, isScoringStateLoading } = useScoring({
    fetchScoringState: fetchScoring,
  });

  const isLoading =
    isKycLoading ||
    isApplicationStateLoading ||
    isLoanLoading ||
    isOfferLoading ||
    isOnboardingStateLoading ||
    isLoanOfferLoading ||
    isScoringStateLoading;

  const facts = useMemo(
    () =>
      isLoading
        ? null
        : buildOnboardingFacts({
            hasActiveLoan: !!activeLoanSummary,
            isOnboardingCompleted,
            isEmailSaved: !!onboardingState?.isEmailCompleted,
            isEmailVerified,
            isTermsAccepted,
            isBcTermsAccepted: !!onboardingState?.isBcTermsAccepted,
            hasDeclaredIncome: !!user?.declaredIncome,
            scoringState,
            isKycValid,
            isKycFailed,
            isQuestionnaireCompleted,
            isProofOfIncomeProcessing: proofOfIncomeProcessing,
            hasFinalOffer: !!applicationState?.hasFinalOffer,
            offer,
            creditOffers: loanOfferState?.creditOffers,
          }),
    [
      isLoading,
      activeLoanSummary,
      isOnboardingCompleted,
      onboardingState?.isEmailCompleted,
      isEmailVerified,
      isTermsAccepted,
      onboardingState?.isBcTermsAccepted,
      user?.declaredIncome,
      scoringState,
      isKycValid,
      isKycFailed,
      isQuestionnaireCompleted,
      proofOfIncomeProcessing,
      applicationState?.hasFinalOffer,
      offer,
      loanOfferState?.creditOffers,
    ],
  );

  return { facts, isLoading };
};
